import { ajv, JSONSchema7WithErrors, TokenContext } from '@cibo/core'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import type { FieldError, FormContextValue, WarningEntry } from 'dynamic-uniforms'
import { EffectiveSchemaBridge, FormContext } from 'dynamic-uniforms'
import { cloneDeep, isEqual, set } from 'lodash'
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoFields } from './AutoFields'
import { CiboAutoFormContext } from './CiboAutoFormContext'
import { dereferenceSchema } from './dereferenceSchema'
import { createWarningsValidator } from './warningsValidator'

function touchedFromModel(model: Record<string, unknown>, prefix = ''): Record<string, boolean> {
  const result: Record<string, boolean> = {}
  for (const key of Object.keys(model)) {
    const path = prefix ? `${prefix}.${key}` : key
    const val = model[key]
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(result, touchedFromModel(val as Record<string, unknown>, path))
    } else {
      result[path] = true
    }
  }
  return result
}

type CiboAutoFormProps = {
  warningSchema?: any
  renderDisplay?: (config: { field: any; itemProps: any }) => ReactNode
  schema: JSONSchema7WithErrors
  model?: any
  onSubmit?: (model: any) => void | Promise<void>
  onChangeModel?: (model: any) => void
  validate?: 'onChange' | 'onSubmit'
  autosave?: boolean
  autosaveDelay?: number
  disabled?: boolean
  readOnly?: boolean
  children?: ReactNode
  tokenContext?: TokenContext
  'data-testid'?: string
}

const stripFormModel = (formSchema: JSONSchema7WithErrors, model: Record<string, unknown>) =>
  new EffectiveSchemaBridge(formSchema as any, model, ajv).stripModel(model) as Record<
    string,
    unknown
  >

const hasPersistedValue = (model: unknown): boolean => {
  if (model === null || typeof model === 'undefined') return false

  if (Array.isArray(model)) {
    return model.length > 0
  }

  if (typeof model !== 'object') {
    return true
  }

  const entries = Object.entries(model as Record<string, unknown>).filter(
    ([, value]) => typeof value !== 'undefined'
  )

  if (entries.length === 0) return false

  if (entries.length === 1 && entries[0][0] === 'value') {
    return hasPersistedValue(entries[0][1])
  }

  return true
}

export function CiboAutoForm({
  model: externalModel,
  schema,
  warningSchema,
  renderDisplay,
  onSubmit,
  onChangeModel,
  validate = 'onSubmit',
  autosave = false,
  autosaveDelay = 300,
  disabled = false,
  readOnly = false,
  children,
  tokenContext,
  'data-testid': dataTestId,
}: CiboAutoFormProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>(() => {
    if (!externalModel || Object.keys(externalModel).length === 0) return {}
    return touchedFromModel(externalModel)
  })
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  // Create wrapper schema that nests user schema under "value"
  const formSchema = useMemo(() => {
    if (!schema?.type) {
      console.error(
        "The configured schema does not have a 'type' property.\n\n",
        JSON.stringify(schema, null, 2),
        '\n\n'
      )
      throw new Error("Schema must include a 'type' property.")
    }

    return {
      type: 'object' as const,
      properties: {
        value: {
          $id: 'schema',
          ...dereferenceSchema(schema, schema),
          'ui-params': {
            ...((schema as any)['ui-params'] || {}),
            hideTitle: true,
          },
        },
      },
      required: ['value'] as string[],
    }
  }, [schema])

  const effectiveWarningSchema = warningSchema ?? (schema as any)?.['ui-params']?.warningSchema

  const warningValidator = useMemo(() => {
    return effectiveWarningSchema ? createWarningsValidator(effectiveWarningSchema) : undefined
  }, [effectiveWarningSchema])

  const [bridge, setBridge] = useState(
    () => new EffectiveSchemaBridge(formSchema, externalModel ?? {}, ajv, tokenContext)
  )
  const [model, setModel] = useState<Record<string, unknown>>(() => externalModel ?? {})
  const [savedModel, setSavedModel] = useState<Record<string, unknown>>(() =>
    stripFormModel(formSchema, (externalModel ?? {}) as Record<string, unknown>)
  )
  const modelRef = useRef(model)
  modelRef.current = model
  const initialBridge = useRef(bridge)
  const [errors, setErrors] = useState<FieldError[]>(() =>
    validate === 'onChange' ? initialBridge.current.validateEffective(externalModel ?? {}) : []
  )
  const [warnings, setWarnings] = useState<Record<string, WarningEntry>>(() => {
    if (!warningValidator || !externalModel || Object.keys(externalModel).length === 0) return {}
    const stripped = initialBridge.current.stripModel(externalModel)
    const rawWarnings = warningValidator((stripped as any).value ?? stripped)
    const prefixed: Record<string, WarningEntry> = {}
    for (const [key, val] of Object.entries(rawWarnings)) {
      prefixed[`value.${key}`] = val
    }
    return prefixed
  })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const lastNotifiedModel = useRef<Record<string, unknown> | null>(null)

  // Sync when external model or schema changes (skip if we caused the change)
  useEffect(() => {
    if (externalModel === lastNotifiedModel.current) {
      return
    }
    const m = externalModel ?? {}
    const newBridge = new EffectiveSchemaBridge(formSchema, m, ajv, tokenContext)
    const stripped = newBridge.stripModel(m) as Record<string, unknown>
    setModel(m)
    setBridge(newBridge)
    setSavedModel(stripped)
    setTouched({})
    if (validate === 'onChange') {
      setErrors(newBridge.validateEffective(m))
    }
    if (Object.keys(m).length > 0) {
      setTouched(prev => ({ ...prev, ...touchedFromModel(m) }))
      if (warningValidator) {
        const rawWarnings = warningValidator((stripped as any).value ?? stripped)
        const prefixed: Record<string, WarningEntry> = {}
        for (const [key, val] of Object.entries(rawWarnings)) {
          prefixed[`value.${key}`] = val
        }
        setWarnings(prefixed)
      }
    }
  }, [externalModel, formSchema, validate, warningValidator])

  const strippedModel = useMemo(() => stripFormModel(formSchema, model), [formSchema, model])

  const hasExistingModel = useMemo(
    () => hasPersistedValue((savedModel as { value?: unknown }).value ?? savedModel),
    [savedModel]
  )

  const isTouched = useMemo(() => Object.values(touched).some(Boolean), [touched])
  const isDirty = useMemo(() => !isEqual(strippedModel, savedModel), [strippedModel, savedModel])

  const onChangeModelRef = useRef(onChangeModel)
  onChangeModelRef.current = onChangeModel
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleChange = useCallback(
    (name: string, value: unknown) => {
      const next = cloneDeep(modelRef.current)
      set(next, name, value)

      const newBridge = new EffectiveSchemaBridge(formSchema, next, ajv, tokenContext)
      const stripped = newBridge.stripModel(next) as Record<string, unknown>

      // Update ref immediately so back-to-back onChange calls
      // within the same event (e.g. setting a sibling field) see
      // each other's changes before React re-renders.
      modelRef.current = stripped

      setModel(stripped)
      setBridge(newBridge)

      const validationErrors = newBridge.validateEffective(stripped)

      if (validate === 'onChange') {
        setErrors(validationErrors)
      }

      if (autosave && validationErrors.length === 0) {
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
        autosaveTimerRef.current = setTimeout(() => {
          onSubmitRef.current?.(stripped)
        }, autosaveDelay)
      }

      if (warningValidator) {
        // warningValidator runs against the user's unwrapped model (outside the "value" wrapper
        // that CiboAutoForm uses internally), so strip it before validating.
        const rawWarnings = warningValidator((stripped as any).value ?? stripped)
        const prefixed: Record<string, WarningEntry> = {}
        for (const [key, val] of Object.entries(rawWarnings)) {
          prefixed[`value.${key}`] = val
        }
        setWarnings(prefixed)
      }

      if (onChangeModelRef.current) {
        lastNotifiedModel.current = stripped
        onChangeModelRef.current(stripped)
      }
    },
    [formSchema, validate, warningValidator, autosave, autosaveDelay]
  )

  const handleSubmit = useCallback(() => {
    setSubmitted(true)
    const currentBridge = new EffectiveSchemaBridge(formSchema, model, ajv, tokenContext)
    const stripped = currentBridge.stripModel(model) as Record<string, unknown>
    const validationErrors = currentBridge.validateEffective(stripped)
    setErrors(validationErrors)

    if (validationErrors.length > 0) return

    if (onSubmit) {
      setSubmitting(true)
      try {
        const result = onSubmit(stripped)
        if (result && typeof (result as Promise<void>).then === 'function') {
          ;(result as Promise<void>)
            .then(() => {
              setSavedModel(stripped)
              setTouched({})
              setSubmitting(false)
            })
            .catch(() => setSubmitting(false))
        } else {
          setSavedModel(stripped)
          setTouched({})
          setSubmitting(false)
        }
      } catch {
        setSubmitting(false)
      }
    }
  }, [formSchema, model, onSubmit])

  const contextValue = useMemo<FormContextValue>(
    () => ({
      bridge,
      model,
      onChange: handleChange,
      onSubmit: handleSubmit,
      errors,
      ajv,
      warnings,
      touched,
      touch,
      submitted,
      submitting,
      disabled,
      readOnly,
      showInlineError: true,
      namePrefix: '',
    }),
    [
      bridge,
      model,
      handleChange,
      handleSubmit,
      errors,
      warnings,
      touched,
      touch,
      submitted,
      submitting,
      disabled,
      readOnly,
    ]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CiboAutoFormContext.Provider value={{ renderDisplay, hasExistingModel, isTouched, isDirty }}>
        <FormContext.Provider value={contextValue}>
          <form
            data-testid={dataTestId}
            noValidate
            onSubmit={(e: FormEvent) => {
              e.preventDefault()
              handleSubmit()
            }}
          >
            {children ?? <AutoFields />}
          </form>
        </FormContext.Provider>
      </CiboAutoFormContext.Provider>
    </LocalizationProvider>
  )
}
